import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

import { DialogService } from 'primeng/dynamicdialog';

import { datetimeFormat } from 'app/constants/date-time-format';
import { HelperService, HTTPService, DateService } from 'app/services';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';
import { currency } from 'app/constants/currency.constants';

@Component({
  selector: 'app-deferred-service-stripe',
  templateUrl: './deferred-service-stripe.component.html',
  styleUrls: ['./deferred-service-stripe.component.scss'],
})
export class DeferredServiceStripeComponent implements OnInit {
  @Input('vehicleId')
  set vehicleId(vehicleId: string) {
    if (typeof vehicleId === 'string') {
      this._vehicleId = vehicleId;
      this.getDeferredServices();
    }
  }
  @Input('orderStatus')
  set orderStatus(orderStatus: string) {
    if (this._vehicleId) {
      this.getDeferredServices();
    }
  }
  @Input('serviceCount')
  set serviceCount(serviceCount: number) {
    if (this.vehicleId && serviceCount !== this._serviceCount) {
      this.getDeferredServices();
    }
  }
  @Input('deferredServices')
  set deferredServices(deferredServices: any[]) {
    if (deferredServices && deferredServices.length >= 0) {
      this.visibleDeferredServices = deferredServices.filter(({ isAddedInRO }) => !isAddedInRO);
      this.hiddenServicesCount = 0;

      if (this.visibleDeferredServices.length > 3) {
        this.hiddenServicesCount = this.visibleDeferredServices.length - 3;
      }

      if (this.visibleDeferredServices.length === 0) {
        this.handleHideServiceStripeClick(true);
      }
    }
  }
  @Input() value: string = '';
  @Input() isArchived: boolean;
  @Input() index: number;
  @Input() isSubletTrackingEnabled: boolean = false;
  @Input('isDeferredServiceAdding')
  set isDeferredServiceAdding(value: boolean) {
    this.isLoading = value;
  } 

  @Output() addDeferredService = new EventEmitter<any>();
  @Output() toggleShowDeferredSerivce = new EventEmitter<any>();
  @Output() setDeferredServices = new EventEmitter<any>();

  @Output() appClickHeader = new EventEmitter<any>();
  @Output() addClickService = new EventEmitter<any>();
  @Output() serviceStripeBtn = new EventEmitter<any>();

  _vehicleId: string = '';

  visibleDeferredServices: any[] = [];
  usd: string = currency.USD;
  hiddenServicesCount: number = 0;
  isLoading: boolean = false;
  _serviceCount: number = 0;

  constructor(private dialogService: DialogService, private httpService: HTTPService, private helperService: HelperService, private dateService: DateService, private currencyPipe: CurrencyPipe) {}

  ngOnInit() {}

  getDeferredServices() {
    if (!this._vehicleId || this.isLoading) {
      return;
    }

    this.isLoading = true;

    this.httpService
      .getOrderDeferredService(this._vehicleId)
      .then(
        (res) => {
          if (res.services && res.services.length >= 0) {
            res.services.forEach((service: any) => {
              service.tooltip = this.createServiceTooltip(service);
            });
            this.setDeferredServices.emit(res.services);
          }
        },
        (err) => {
          console.log(err);
        }
      )
      .finally(() => {
        this.isLoading = false;
      });
  }

  handleAddServiceClick(service: any) {
    this.helperService.serviceEventGenerator(
      service.estimate?.estimateCode,
      service.title ? service.title : service.titleFR,
      true,  
    );
    this.isLoading = true;
    this.addDeferredService.emit(service);
  }

  handleHideServiceStripeClick(isForcedClose: boolean = false) {
    this.toggleShowDeferredSerivce.emit(isForcedClose);
  }

  handleViewAllClick() {
    this.appClickHeader.emit(this.value);
  }

  handleConfirmServiceDelete(service: any) {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Delete ${service.title || 'Untitled'}`,
        message: 'Are you sure you want to delete this service?',
        button: {
          success: { title: 'Delete' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.deleteDeferredService(service._id);
      }
      onClose.unsubscribe();
    });
  }

  deleteDeferredService(serviceId: string) {
    this.isLoading = true;
    this.httpService
      .deleteCustomerDeferredService(serviceId)
      .then(
        (res) => {
          this.isLoading = false;
          this.getDeferredServices();
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      )
      .finally(() => {
        this.isLoading = false;
      });
  }

  openOrder(estimateId: string) {
    if (!estimateId) return;

    this.helperService.openPageInNewExistingTab(`/estimate/${estimateId}`, estimateId);
  }

  createServiceTooltip(service: any) {
    const counts = {
      part: 0,
      labor: 0,
      tire: 0,
      other: 0,
      sublet: 0,
    };

    service.items.forEach(({ type, count }) => {
      counts[type] += count;
    });

    let inspectionItemCount = 0;
    let hasInspection = false;

    if (service.inspection && service.inspection.inspectionId && service.inspection.inspectionId.category) {
      hasInspection = true;
      service.inspection.inspectionId.category.forEach(({ inspectionItems }) => {
        inspectionItemCount += (inspectionItems && inspectionItems.length) || 0;
      });
    }

    let tooltip = `
    <div class="tooltip-seondary">
      <div class="tooltip-secondary-row font-14 font-600 divider-bottom d-flex flex-nowrap">
        <i class="fal fa-file-alt mt-1 font-secondary-800"></i>
        <span class="font-gray-45">${service.title}</span>
      </div>
    </div>

    <div class="tooltip-2">
      <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex tech-service-wrapper">
        <div class="font-gray-35 d-flex pr-2 defferred-label-wrapper">
          <i class="fal fa-file-alt align-self-start" style="margin-top: 2px;"></i>
          <span class="deferred-label font-10 font-gray-35">
            RO# ${service.estimate && service.estimate.estimateCode}
          </span>
        </div>
      </div>

      <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex tech-service-wrapper">
        <div class="font-gray-35 d-flex pr-2 defferred-label-wrapper">
          <i class="fal fa-calendar-alt align-self-start" style="margin-top: 2px;"></i>
          <span class="deferred-label font-10 font-gray-35">
            <span class="deferred-text">Deferred</span>
            <i class="fal fa-minus pl-1"></i>
          </span>
        </div>
        <div class="deferred-field font-10 font-gray-35">
          <span>
          ${this.dateService.formatDate(this.dateService.getCurrentDateTimeTz(new Date(service.createdAt).getTime()), datetimeFormat.mmmdyyhhmmaa)}
          </span>
        </div>
      </div>

      <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex tech-service-wrapper">
        <div class="font-gray-35 d-flex pr-2 defferred-label-wrapper">
          <i class="fal fa-clock align-self-start" style="margin-top: 2px;"></i>
          <span class="deferred-label font-10 font-gray-35">
            <span class="deferred-text">
              ${counts.labor} hour${counts.labor > 1 ? 's' : ''}
            </span>
            <i class="fal fa-minus pl-1"></i>
          </span>
        </div>
        <div class="deferred-field font-10 font-gray-35">
          <span>
            ${this.currencyPipe.transform(service.serviceTotal.labor || 0, this.usd)}
          </span>
        </div>
      </div>

      <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex tech-service-wrapper">
        <div class="font-gray-35 d-flex pr-2 defferred-label-wrapper">
          <i class="fal fa-tools align-self-start" style="margin-top: 2px;"></i>
          <span class="deferred-label font-10 font-gray-35">
            <span class="deferred-text">
              ${counts.part} part${counts.part > 1 ? 's' : ''}
            </span>
            <i class="fal fa-minus pl-1"></i>
          </span>
        </div>
        <div class="deferred-field font-10 font-gray-35">
          <span>
          ${this.currencyPipe.transform(service.serviceTotal.part || 0, this.usd)}
          </span>
        </div>
      </div>

      <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex tech-service-wrapper">
        <div class="font-gray-35 d-flex pr-2 defferred-label-wrapper">
          <i class="fal fa-tire align-self-start" style="margin-top: 2px;"></i>
          <span class="deferred-label font-10 font-gray-35">
            <span class="deferred-text">
              ${counts.tire} tire${counts.tire > 1 ? 's' : ''}
            </span>
            <i class="fal fa-minus pl-1"></i>
          </span>
        </div>
        <div class="deferred-field font-10 font-gray-35">
          <span>
            ${this.currencyPipe.transform(service.serviceTotal.tire || 0, this.usd)}
          </span>
        </div>
      </div>

      <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex tech-service-wrapper">
        <div class="font-gray-35 d-flex pr-2 defferred-label-wrapper">
          <i class="fal fa-list align-self-start" style="margin-top: 2px;"></i>
          <span class="deferred-label font-10 font-gray-35">
            <span class="deferred-text">
              ${counts.other} other${counts.other > 1 ? 's' : ''}
            </span>
            <i class="fal fa-minus pl-1"></i>
          </span>
        </div>
        <div class="deferred-field font-10 font-gray-35">
          <span>
            ${this.currencyPipe.transform(service.serviceTotal.other || 0, this.usd)}
          </span>
        </div>
      </div>

      <div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex tech-service-wrapper">
        <div class="font-gray-35 d-flex pr-2 defferred-label-wrapper">
          <i class="fal fa-ballot-check align-self-start" style="margin-top: 2px;"></i>
          <span class="deferred-label font-10 font-gray-35">
            <span class="deferred-text">
            ${hasInspection ? 1 : 0} inspection
            </span>
            <i class="fal fa-minus pl-1"></i>
          </span>
        </div>
        <div class="deferred-field font-10 font-gray-35">
          <span>
          ${inspectionItemCount} item${inspectionItemCount > 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </div>
    `;

    if(this.isSubletTrackingEnabled) {
      tooltip += `<div class="tooltip-secondary-row seondary-row-padding font-12 font-gray-35 d-flex tech-service-wrapper">
      <div class="font-gray-35 d-flex pr-2 defferred-label-wrapper">
        <i class="fal fa-store align-self-start" style="margin-top: 2px;"></i>
        <span class="deferred-label font-10 font-gray-35">
          <span class="deferred-text">
            ${counts.sublet} sublet${counts.sublet > 1 ? 's' : ''}
          </span>
          <i class="fal fa-minus pl-1"></i>
        </span>
      </div>
      <div class="deferred-field font-10 font-gray-35">
        <span>
          ${this.currencyPipe.transform(service.serviceTotal?.sublet || 0, this.usd)}
        </span>
      </div>
    </div>`;
    }

    return tooltip;
  }
}
